/**
 * CartAPI — thin wrappers around Shopify's cart mutation endpoints.
 *
 * Each method handles CartStore cache management automatically:
 *   CartAPI.update()  — caches the full-cart response via CartStore.setCache()
 *   CartAPI.add()     — invalidates CartStore (add returns line items, not full cart)
 *
 * Depends on cart-store.js (window.CartStore) being loaded first.
 *
 * Usage:
 *   CartAPI.update({ updates: { "key": 0 }, attributes: { "delivery-method": "Shipping" } })
 *   CartAPI.add([{ id: 123, quantity: 1, properties: { _isglobalpallet: "true" } }])
 */
window.CartAPI = (function () {

  // Debug tracing for cart writes. ON by default so we can diagnose 429s in the
  // wild; set `window.CART_DEBUG = false` in the console to silence it.
  function _debugOn() { return window.CART_DEBUG !== false; }

  var _writeSeq = 0;          // running counter of all writes (add + update)
  var _lastWriteAt = 0;       // timestamp of the previous write (ms)
  var _lastAddKey = '';       // payload signature of the previous add

  function _ts() { return new Date().toISOString().slice(11, 23); }

  // Logs WHO called a cart write, WITH WHAT, and how long since the last write.
  // Returns the sequence number so the matching HTTP result can be correlated.
  function _traceWrite(label, color, payload) {
    var seq = ++_writeSeq;
    if (!_debugOn()) return seq;
    var now = Date.now();
    var gap = _lastWriteAt ? (now - _lastWriteAt) + 'ms since last write' : 'first write';
    _lastWriteAt = now;
    try {
      console.groupCollapsed(
        '%c[CartAPI #' + seq + '] ' + label + ' @ ' + _ts() + '  (' + gap + ')',
        'color:' + color + ';font-weight:bold;'
      );
      console.log('payload:', payload);
      // Stack of the caller chain — shows which function fired this write.
      console.log('called from:\n' + (new Error('cart write call site').stack || '(no stack)'));
      console.groupEnd();
    } catch (e) { /* console may be unavailable */ }
    return seq;
  }

  // All cart writes go through CartQueue (global serializer + 429 backoff).
  // Falls back to plain fetch if CartQueue is not loaded.
  function _cartFetch(url, init) {
    return (window.CartQueue ? window.CartQueue.fetch : fetch)(url, init);
  }

  /**
   * POST /cart/update.js
   * body can include: updates (line-key → qty), attributes, note — any combination.
   * Caches the full-cart response in CartStore so the next fetchCart() is free.
   * Returns a Promise that resolves to the updated cart object (or null on error).
   */
  function update(body, opts) {
    var _seq = _traceWrite('update.js', '#06c', body);
    function doFetch() {
      var init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      };
      if (opts && opts.failFastOn429) init.failFastOn429 = true;
      return _cartFetch('/cart/update.js', init);
    }
    return doFetch()
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (text) {
            console.error('[CartAPI #' + _seq + '] /cart/update.js HTTP ' + r.status + ':', text.slice(0, 300));
            return null;
          });
        }
        return r.json().then(function (cart) {
          if (window.CartStore) window.CartStore.setCache(cart);
          return cart;
        });
      })
      .catch(function (e) {
        console.error('[CartAPI #' + _seq + '] /cart/update.js failed:', e);
        return null;
      });
  }

  /**
   * POST /cart/add.js
   * items: array of { id, quantity, properties? }
   * /cart/add.js returns line items not the full cart, so CartStore is invalidated
   * and the next fetchCart() will GET fresh post-add data.
   * Returns a Promise that resolves to the add response (or null on error).
   */
  function add(items, opts) {
    // Build a payload signature so we can flag rapid duplicate adds — the
    // classic cause of a /cart/add.js 429 burst.
    var _key = '';
    try {
      _key = (items || []).map(function (it) {
        return it.id + 'x' + it.quantity;
      }).sort().join('|');
    } catch (e) { _key = ''; }

    var _sinceLast = _lastWriteAt ? (Date.now() - _lastWriteAt) : Infinity;
    var _seq = _traceWrite('add.js  [' + (items ? items.length : 0) + ' item(s)]', '#0a7', items);

    if (_debugOn() && _key && _key === _lastAddKey && _sinceLast < 1500) {
      console.warn(
        '[CartAPI #' + _seq + '] ⚠ DUPLICATE add.js — same items (' + _key +
        ') fired ' + _sinceLast + 'ms after the previous add. This is the likely 429 cause.'
      );
    }
    _lastAddKey = _key;

    function doFetch() {
      var init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      };
      if (opts && opts.failFastOn429) init.failFastOn429 = true;
      return _cartFetch('/cart/add.js', init);
    }
    return doFetch()
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (text) {
            console.error('[CartAPI #' + _seq + '] /cart/add.js HTTP ' + r.status + ':', text.slice(0, 300));
            return null;
          });
        }
        return r.json().then(function (result) {
          if (window.CartStore) window.CartStore.invalidate();
          return result;
        });
      })
      .catch(function (e) {
        console.error('[CartAPI #' + _seq + '] /cart/add.js failed:', e);
        return null;
      });
  }

  return { update: update, add: add };
})();
